import type { GraphQLError } from 'graphql';
import { ApolloError } from 'apollo-client';

import type { Mark } from '@atlaskit/editor-prosemirror/model';

import { ApolloErrorInterop } from '@confluence/apollo-relay-interop/entry-points/ApolloErrorInterop.ts';
import { COMMENT_BATCH_SIZE } from '@confluence/comments-data/entry-points/useActiveInlineCommentsQuery.ts';
import {
	CommentActionType,
	CommentType,
} from '@confluence/comments-data/entry-points/useCommentsData.ts';
import { getValidDate } from '@confluence/comments-util/entry-points/dateUtils.ts';
import type {
	AnnotationStatus,
	CommentData as LegacyCommentData,
	CommentsDataMap as LegacyCommentsDataMap,
	RemovedThreadsCommentTypeMap,
	ReplyData as LegacyReplyData,
} from '@confluence/comments-data/entry-points/useCommentsData.ts';
import type {
	CommentData as NewCommentData,
	CommentsDataMap as NewCommentsDataMap,
	ReplyData as NewReplyData,
} from '@confluence/comments-data-manager/entry-points/commentDataManagerUtils';
import { fg } from '@confluence/feature-gating';
import type {
	CommentInlineLocation,
	CommentFooterLocation,
	ActiveCommentInlineResolveProperties,
} from '@confluence/comments-panel-queries/entry-points/ActiveCommentsQuery.ts';
import type { CommentInlineResolveProperties } from '@confluence/inline-comments-queries/entry-points/ActiveInlineCommentsQuery.ts';

type CommentData = LegacyCommentData | NewCommentData;
type ReplyData = LegacyReplyData | NewReplyData;
type ReplyDataOrNull = ReplyData | null | undefined;

import { SortValues } from './useCommentsPanel.ts';

const SCROLLED_COMMENTS_HEADER_GAP_SIZE_IN_PIXELS = 8;
const NUM_HIDDEN_PIXELS_TO_BE_CONSIDERED_OUT_OF_VIEW = 60;

const getTotalOffsetAboveCommentsPanelThreads = (generalCommentsHeaderHeight: number): number => {
	let objectSidebarPanelHeader = document.querySelector(
		"[data-testid='object-sidebar-panel-header']",
	);
	let additionalOffset = 0;
	if (!objectSidebarPanelHeader) {
		objectSidebarPanelHeader = document.querySelector(
			"[data-testid='scrolled-object-sidebar-panel-header']",
		);
		additionalOffset = SCROLLED_COMMENTS_HEADER_GAP_SIZE_IN_PIXELS;
	}

	const objectSidebarPanelHeaderHeight =
		objectSidebarPanelHeader?.getBoundingClientRect().height ?? 0;
	const objectHeaderHeight =
		document.querySelector("[data-testid='object-header-container']")?.getBoundingClientRect()
			.height ?? 0;
	const topNavContainerHeight =
		document.querySelector("[data-testid='grid-topNav']")?.getBoundingClientRect().height ?? 0;
	const bannerContainerHeight =
		document.querySelector("[data-testid='banner-container']")?.getBoundingClientRect().height ?? 0;
	const editorControlsOnboardingBannerHeight =
		document
			.querySelector("[data-testid='editor-controls-onboarding-banner']")
			?.getBoundingClientRect().height ?? 0;
	const announcementBannerHeight =
		document.querySelector("[id='AkBanner']")?.getBoundingClientRect().height ?? 0;

	return (
		objectSidebarPanelHeaderHeight +
		objectHeaderHeight +
		topNavContainerHeight +
		bannerContainerHeight +
		additionalOffset +
		generalCommentsHeaderHeight +
		editorControlsOnboardingBannerHeight +
		announcementBannerHeight
	);
};

export const shouldScrollCommentThreadToTop = (
	commentThreadElement: Element,
	commentType?: CommentType,
): boolean => {
	const commentThreadRect = commentThreadElement.getBoundingClientRect();
	const generalCommentsHeaderHeight =
		commentType === CommentType.GENERAL
			? (document
					.querySelector("[data-testid='comment-section-separator']")
					?.getBoundingClientRect().height ?? 0)
			: 0;
	const totalPaddingAboveCommentThreadView = getTotalOffsetAboveCommentsPanelThreads(
		generalCommentsHeaderHeight,
	);

	const objectSidebarPanelFooter = document.querySelector(
		"[data-testid='object-sidebar-panel-footer']",
	);
	const objectSidebarPanelFooterHeight =
		objectSidebarPanelFooter?.getBoundingClientRect().height ?? 0;
	const viewportTop = totalPaddingAboveCommentThreadView;
	const viewportBottom = window.innerHeight - objectSidebarPanelFooterHeight;
	const commentTopInView = commentThreadRect.top >= viewportTop;
	const commentBottomInView = commentThreadRect.bottom <= viewportBottom;
	if (commentTopInView && commentBottomInView) {
		// Comment is 100% in view
		return false;
	} else if (!commentTopInView && commentBottomInView) {
		// Comment is <100% in view and the hidden part is the top part of the comment
		return true;
	} else if (commentTopInView && !commentBottomInView) {
		// Comment is <100% in view and the hidden part is the bottom of the comment
		const hiddenBottomHeight = commentThreadRect.bottom - viewportBottom;
		if (hiddenBottomHeight <= NUM_HIDDEN_PIXELS_TO_BE_CONSIDERED_OUT_OF_VIEW) {
			// The hidden part is <= 60 pixels
			return false;
		} else {
			// The hidden part is > 60 pixels
			return true;
		}
	} else {
		// Comment is <100% in view and the hidden part is the top part of the comment
		return true;
	}
};

// This function is used when we want the entire comment thread to be in view, such as after the user clicks the reply button
export const isCommentThreadReplyOutOfView = (
	commentThreadElement: Element,
	commentType?: CommentType,
): boolean => {
	const commentThreadRect = commentThreadElement.getBoundingClientRect();

	const generalCommentsHeaderHeight =
		commentType === CommentType.GENERAL
			? (document
					.querySelector("[data-testid='comment-section-separator']")
					?.getBoundingClientRect().height ?? 0)
			: 0;

	const objectSidebarPanelFooter = document.querySelector(
		"[data-testid='object-sidebar-panel-footer']",
	);
	const objectSidebarPanelFooterHeight =
		objectSidebarPanelFooter?.getBoundingClientRect().height ?? 0;

	return (
		// Element is partially out of view below the comments panel viewport
		commentThreadRect.bottom + generalCommentsHeaderHeight >
		window.innerHeight - objectSidebarPanelFooterHeight
	);
};

export enum LocationValue {
	PANEL = 'panel',
	FOOTER = 'footer',
	STANDALONE = 'standalone',
}

/**
 * Watches for a specific comment container to appear in the DOM and scrolls to it when found
 * Only active when confluence-fe-comments-deep-link-behavior feature gate is enabled
 */
const watchForCommentContainerAndScroll = (commentContainerToFocus: string): (() => void) => {
	if (!fg('confluence-fe-comments-deep-link-behavior')) {
		return () => {}; // Return empty cleanup function if feature gate is disabled
	}

	let isDisconnected = false;
	let commentFound = false;

	// Find the most specific container to observe
	const commentsPanelListContainer = document.querySelector(
		'[data-testid="comments-panel-list-container"]',
	);
	const targetContainer = commentsPanelListContainer || document.body;

	const observer = new MutationObserver(() => {
		if (isDisconnected || commentFound) return;

		// Look for the comment element directly
		const commentElement = targetContainer.querySelector(
			`[data-comment-id="${commentContainerToFocus}"]`,
		);

		if (commentElement) {
			commentFound = true;
			commentElement.scrollIntoView({
				behavior: 'smooth',
				block: 'center',
				inline: 'center',
			});
			isDisconnected = true;
			observer.disconnect();
		}
	});

	// Start observing only the specific container
	observer.observe(targetContainer, {
		childList: true,
		subtree: true,
		attributes: true,
		attributeFilter: ['data-comment-id'],
	});

	// Return cleanup function
	return () => {
		if (!isDisconnected) {
			observer.disconnect();
			isDisconnected = true;
		}
	};
};

/**
 * @deprecated This helper method is deprecated. Use handleCommentsPanelNavigation from @confluence/comments-util instead.
 * This file is kept for backward compatibility but returns an empty object.
 * All consumers should migrate to the new unified navigation functions.
 */

export const scrollCommentsPanel = ({
	containerId,
	commentMarkerRef,
	commentType,
	scrollToEnd,
	hasKeyPressed,
	annotationClicked,
	scrollOnLoad,
	commentContainerToFocus,
}: {
	containerId: string;
	commentMarkerRef: string;
	commentType?: CommentType;
	scrollToEnd?: boolean;
	hasKeyPressed?: boolean;
	annotationClicked?: boolean;
	scrollOnLoad?: boolean;
	commentContainerToFocus?: string;
}) => {
	if (fg('confluence_comments_unified_navigation')) {
		return;
	}

	const container = document.querySelector(`[data-testid='${containerId}']`);
	if (container) {
		const element = document.querySelector(`[data-testid='${commentMarkerRef}']`);

		if (commentContainerToFocus) {
			watchForCommentContainerAndScroll(commentContainerToFocus);
			return;
		}

		if (element) {
			const activeElement = document.activeElement;
			const isReplyButton = activeElement?.getAttribute('data-testid') === 'comment-reply-button';
			if (isReplyButton) {
				const replyContainer = document.querySelector(
					'[data-testid=comments-panel-new-reply-container]',
				);
				replyContainer?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
				return;
			}
			// If triggered by keyboard navigation or focus or if annotationClicked is true or triggered by a focused loading
			if (hasKeyPressed || annotationClicked || scrollOnLoad) {
				if (fg('confluence_frontend_content_wrapper')) {
					if (
						(scrollToEnd && isCommentThreadReplyOutOfView(element, commentType)) ||
						shouldScrollCommentThreadToTop(element, commentType)
					) {
						element.scrollIntoView({ behavior: 'smooth', block: scrollToEnd ? 'end' : 'start' });
					}
				} else {
					element.scrollIntoView({ behavior: 'smooth', block: 'start' });
				}
				return;
			}
		}
	}
};

export const getAnnotationsToLoad = (orderedActiveAnnotationIdList: AnnotationStatus[]) => {
	return orderedActiveAnnotationIdList
		.slice(0, Math.min(orderedActiveAnnotationIdList.length, COMMENT_BATCH_SIZE))
		.filter((item) => !item.isLoaded) // only get the ones that are not loaded yet
		.map((item) => item.threadKey);
};

const getDanglingComments = ({
	commentsDataMap,
	annotationsSet,
	openCommentThreads,
}: {
	commentsDataMap: LegacyCommentsDataMap | NewCommentsDataMap;
	annotationsSet: Set<string> | undefined;
	openCommentThreads: CommentData[];
}) => {
	// Add dangling comments and handle real-time changes
	for (const threadKey in commentsDataMap.inline) {
		const commentThread = commentsDataMap.inline[threadKey];
		const isDanglingComment = (commentThread?.location as CommentInlineLocation)
			?.inlineResolveProperties?.isDangling;

		if (commentThread?.isOpen) {
			// if the comment is already dangling, we want to add it to the list
			if (isDanglingComment) {
				const markerRef = (commentThread?.location as CommentInlineLocation)?.inlineMarkerRef;

				// comment is no longer dangling if it has a marker ref and is in the annotations set
				if (markerRef && annotationsSet && annotationsSet.has(markerRef)) {
					const inlineLocation = commentThread.location as CommentInlineLocation;
					const updatedCommentThread = {
						...commentThread,
						location: {
							...commentThread.location,
							inlineResolveProperties: {
								...(inlineLocation.inlineResolveProperties as ActiveCommentInlineResolveProperties),
								isDangling: false,
							} as CommentInlineResolveProperties,
						},
					} as CommentData;
					openCommentThreads.push(updatedCommentThread);
				} else {
					openCommentThreads.push(commentThread);
				}

				continue;
			}

			// check for real time dangling comments
			const markerRef = (commentThread?.location as CommentInlineLocation)?.inlineMarkerRef;

			// comment is dangling if it has a marker ref and is not in the annotations set
			// deleted and resolved comments are not in commentsDataMap so we know it's only dangling scenario
			// we do this here so the view will update immediately, but the actual mutation is handled in useDanglingOperations
			if (markerRef && annotationsSet && !annotationsSet.has(markerRef)) {
				const inlineLocation = commentThread.location as CommentInlineLocation;
				const updatedCommentThread = {
					...commentThread,
					location: {
						...commentThread.location,
						inlineResolveProperties: {
							...(inlineLocation.inlineResolveProperties as ActiveCommentInlineResolveProperties),
							isDangling: true,
						} as CommentInlineResolveProperties,
					},
				} as CommentData;
				openCommentThreads.push(updatedCommentThread);
			}
		}
	}
};

// organize a list of open comment threads that we have data for
export const getOpenCommentThreads = ({
	commentsDataMap,
	orderedActiveAnnotationIdList,
	removedThreadsMap,
	showInlineComments,
	showGeneralComments,
	location = LocationValue.PANEL,
}: {
	commentsDataMap: LegacyCommentsDataMap | NewCommentsDataMap;
	orderedActiveAnnotationIdList: AnnotationStatus[];
	removedThreadsMap: RemovedThreadsCommentTypeMap;
	showInlineComments: boolean;
	showGeneralComments: boolean;
	location?: LocationValue;
}) => {
	const openCommentThreads: (LegacyCommentData | NewCommentData)[] = [];
	const annotationsSet = new Set(orderedActiveAnnotationIdList.map((item) => item.threadKey));

	// inline comments
	if (showInlineComments && location === LocationValue.PANEL) {
		const removedAnnotationIds = new Set(Object.values(removedThreadsMap.inline));
		const filteredActiveAnnotationIds = orderedActiveAnnotationIdList.filter(
			(item) => !removedAnnotationIds.has(item.threadKey),
		);
		const totalAnnotationsCount =
			filteredActiveAnnotationIds.length + Object.keys(removedThreadsMap).length;

		let activeIndex = 0; // Track position in filteredActiveAnnotationIds
		for (let i = 0; i < totalAnnotationsCount; i++) {
			let threadKey = '';

			if (removedThreadsMap.inline.hasOwnProperty(i)) {
				threadKey = removedThreadsMap.inline[i];
				const commentThread = commentsDataMap.inline[threadKey];
				if (commentThread) {
					openCommentThreads.push(commentThread);
				}
			} else if (activeIndex < filteredActiveAnnotationIds.length) {
				threadKey = filteredActiveAnnotationIds[activeIndex].threadKey;
				const commentThread = commentsDataMap.inline[threadKey];

				// dangling comments had a marker ref and will be inside commentsDataMap, so we want to ignore these first
				const isDanglingComment =
					(commentThread?.location as CommentInlineLocation)?.inlineResolveProperties?.isDangling &&
					fg('confluence_frontend_content_wrapper');

				// Don't add if it's dangling or might become dangling (has marker ref but not in annotations set)
				const markerRef = (commentThread?.location as CommentInlineLocation)?.inlineMarkerRef;
				const mightBecomeDangling = markerRef && annotationsSet && !annotationsSet.has(markerRef);

				if (commentThread?.isOpen && !isDanglingComment && !mightBecomeDangling) {
					openCommentThreads.push(commentThread);
				}
				activeIndex++;
			}
		}

		if (fg('confluence_frontend_content_wrapper')) {
			getDanglingComments({
				commentsDataMap,
				openCommentThreads,
				annotationsSet,
			});
		}
	}

	// general comments
	if (showGeneralComments || location === LocationValue.FOOTER) {
		// We can just push these in the order we have them because we have no concept of "active"
		const generalComments = Object.values(commentsDataMap.general).filter((generalComment: CommentData) => {
			return (
				generalComment.isOpen ||
				generalComment.wasRemovedByAnotherUser === CommentActionType.DELETE_COMMENT
			);
		});
		openCommentThreads.push(...generalComments);
	}

	return openCommentThreads;
};

// organize a list of unread comment threads that we have data for
export const getUnreadCommentThreads = ({
	numUnreadComments,
	commentsDataMap,
	orderedActiveAnnotationIdList,
	removedThreadsMap,
	showInlineComments,
	showGeneralComments,
	location = LocationValue.PANEL,
}: {
	numUnreadComments: number;
	commentsDataMap: LegacyCommentsDataMap | NewCommentsDataMap;
	orderedActiveAnnotationIdList: AnnotationStatus[];
	removedThreadsMap: RemovedThreadsCommentTypeMap;
	showInlineComments: boolean;
	showGeneralComments: boolean;
	location?: LocationValue;
}) => {
	const unreadCommentThreads: (LegacyCommentData | NewCommentData)[] = [];

	if (
		(Object.keys(commentsDataMap.inline).length > 0 ||
			Object.keys(commentsDataMap.general).length > 0) &&
		(numUnreadComments > 0 || Object.keys(removedThreadsMap.inline).length > 0)
	) {
		// inline comments
		if (showInlineComments && location === LocationValue.PANEL) {
			const removedAnnotationIds = new Set(Object.values(removedThreadsMap.inline));
			const filteredActiveAnnotationIds = orderedActiveAnnotationIdList.filter(
				(item) => !removedAnnotationIds.has(item.threadKey),
			);
			const totalAnnotationsCount =
				filteredActiveAnnotationIds.length + Object.keys(removedThreadsMap.inline).length;

			let activeIndex = 0; // Track position in filteredActiveAnnotationIds

			for (let i = 0; i < totalAnnotationsCount; i++) {
				let threadKey = '';

				if (removedThreadsMap.inline.hasOwnProperty(i)) {
					threadKey = removedThreadsMap.inline[i];
					const commentThread = commentsDataMap.inline[threadKey];
					if (
						commentThread?.isOpen &&
						(commentThread?.isUnread ||
							commentThread?.replies?.some((reply: ReplyDataOrNull) => reply && reply.isUnread))
					) {
						unreadCommentThreads.push(commentThread);
					}
				} else if (activeIndex < filteredActiveAnnotationIds.length) {
					threadKey = filteredActiveAnnotationIds[activeIndex].threadKey;
					const commentThread = commentsDataMap.inline[threadKey];
					if (
						commentThread?.isOpen &&
						(commentThread?.isUnread ||
							commentThread?.replies?.some((reply: ReplyDataOrNull) => reply && reply.isUnread))
					) {
						unreadCommentThreads.push(commentThread);
					}
					activeIndex++;
				}
			}
		}

		// general comments
		if (showGeneralComments) {
			// We can just push these in the order we have them because we have no concept of "active"
			const generalComments = Object.values(commentsDataMap.general).filter((generalComment: CommentData) => {
				return (
					(generalComment.isOpen ||
						generalComment.wasRemovedByAnotherUser === CommentActionType.DELETE_COMMENT) &&
					(generalComment.isUnread ||
						generalComment.replies.some((reply: ReplyDataOrNull) => reply && reply.isUnread))
				);
			});
			unreadCommentThreads.push(...generalComments);
		}
	}

	return unreadCommentThreads;
};

export const getResolvedCommentThreads = ({
	commentsDataMap,
	showInlineComments,
	showGeneralComments,
	location = LocationValue.PANEL,
}: {
	commentsDataMap: LegacyCommentsDataMap | NewCommentsDataMap;
	showInlineComments: boolean;
	showGeneralComments: boolean;
	location?: LocationValue;
}) => {
	const resolvedCommentThreads: (LegacyCommentData | NewCommentData)[] = [];
	// inline comments
	if (showInlineComments && location === LocationValue.PANEL) {
		for (const key in commentsDataMap.inline) {
			const comment = commentsDataMap.inline[key];
			if (comment.isOpen === false) {
				resolvedCommentThreads.push(comment);
			}
		}
	}

	// general comments
	if (showGeneralComments) {
		for (const key in commentsDataMap.general) {
			const comment = commentsDataMap.general[key];
			if (comment.isOpen === false) {
				resolvedCommentThreads.push(comment);
			}
		}
	}

	return resolvedCommentThreads;
};

const getCommentResolvedOrCreatedTime = (comment: LegacyCommentData | NewCommentData): number => {
	const commentLocation = comment.location;
	const resolvedTime =
		(commentLocation as CommentInlineLocation)?.inlineResolveProperties?.resolvedTime ||
		(commentLocation as CommentFooterLocation)?.commentResolveProperties?.resolvedTime; // from the BE, this is for both reopened and resolved time, in milliseconds
	return resolvedTime || getValidDate(comment.createdAtNonLocalized).getTime(); // getTime() returns milliseconds
};

export const getSortedCommentThreads = (
	currentSort: SortValues,
	commentThreads: CommentData[],
): CommentData[] => {
	if (currentSort === SortValues.MOST_RECENT) {
		return [...commentThreads].sort(
			(a, b) => getCommentResolvedOrCreatedTime(b) - getCommentResolvedOrCreatedTime(a),
		);
	}

	return commentThreads;
};

export const getAnnotationThreadKeys = (annotations: AnnotationStatus[]) =>
	annotations.map((annotation) => annotation.threadKey);

export const getMarkIds = (marks: Mark[] = []) => marks.map((mark) => mark.attrs.id);

export const getCommentsSortedOldestToNewest = (
	commentA: LegacyCommentData | NewCommentData | LegacyReplyData | NewReplyData,
	commentB: LegacyCommentData | NewCommentData | LegacyReplyData | NewReplyData,
) =>
	getValidDate(commentA?.createdAtNonLocalized).getTime() -
	getValidDate(commentB?.createdAtNonLocalized).getTime();

// Used to determine if a GQL query error only contains reactionsSummary errors.
// If true, we can ignore the error and still render the comments data that's fetched successfully.
export const onlyHasReactionsSummaryErrors = (error: any) => {
	if (!error) {
		// if falsy, consider it as NOT containing non-reactionsSummary errors
		return true;
	} else if (error instanceof ApolloErrorInterop || error instanceof ApolloError) {
		// TODO we will need to migrate to ApolloRelayInterops to get proper error type of ApolloErrorInterop instead of ApolloError
		// after migration, we can remove the instanceof ApolloError check here
		const hasOtherGqlErrors =
			error.graphQLErrors?.filter((e: GraphQLError) => !e.path?.includes('reactionsSummary'))
				?.length > 0;

		return !hasOtherGqlErrors && !error.networkError;
	} else {
		// if truthy but not an ApolloErrorInterop, consider it as containing non-reactionsSummary errors
		return false;
	}
};
